import type * as GeoJSON from "geojson";

import {
  SceneLayerEntry,
  StoryCamera,
  StoryContent,
  StoryScene,
  StoryBlock,
  StoryScope,
} from "../../types/storyMap";
import { boundaryToFeature, StoryBoundaryInput } from "./storyLayers";
import { type Bbox, bboxPolygon, unionBbox } from "../projectListing/projectBbox";
import { toDisplayUnitLabel, UnitSystem } from "../../utils/units";
import {
  defaultSceneAnimation,
  defaultStoryContent,
  newBlock,
  newScene,
} from "./storyDefaults";

/**
 * Story templates.
 *
 * The builder's real difficulty isn't any one control — it's facing an empty
 * story and having to know that a scene is a scroll step, that the map state is
 * per-scene, and that a "mode" decides what the map even is. A template answers
 * all three by example: the author opens something that already renders and
 * edits it, rather than composing it from parts.
 *
 * Templates are generated against the project's OWN strategies, simulations and
 * boundary, so the result is a live story rather than a lorem-ipsum skeleton.
 * Anything a template can't source (a strategy, a successful simulation) is
 * declared in `requires` and the picker disables the tile with the reason.
 */

// ---------------------------------------------------------------------------
// Camera derivation
// ---------------------------------------------------------------------------

/** Mapbox tile size — the unit its zoom levels are defined against. */
const WORLD_PX = 512;

/** The builder/viewer map pane is roughly this size at a typical desktop width. */
const FIT_VIEWPORT = { width: 900, height: 620 };
const FIT_PADDING_PX = 48;

function bboxOf(
  feature: GeoJSON.Feature
): [number, number, number, number] | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const walk = (coords: unknown) => {
    if (!Array.isArray(coords)) return;
    if (
      coords.length >= 2 &&
      typeof coords[0] === "number" &&
      typeof coords[1] === "number"
    ) {
      west = Math.min(west, coords[0]);
      east = Math.max(east, coords[0]);
      south = Math.min(south, coords[1]);
      north = Math.max(north, coords[1]);
      return;
    }
    coords.forEach(walk);
  };
  walk((feature.geometry as { coordinates?: unknown } | null)?.coordinates);
  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;
  return [west, south, east, north];
}

const mercatorY = (lat: number): number => {
  const clamped = Math.min(Math.max(lat, -85.05112878), 85.05112878);
  const sin = Math.sin((clamped * Math.PI) / 180);
  return Math.log((1 + sin) / (1 - sin)) / 2;
};

/**
 * Zoom that fits a bbox into the map pane — the same Web Mercator fit Mapbox's
 * own fitBounds uses, done here because templates need a camera value to store
 * in content before any map exists to ask.
 */
function zoomForBbox(bbox: [number, number, number, number]): number {
  const [west, south, east, north] = bbox;
  const latFraction = (mercatorY(north) - mercatorY(south)) / (2 * Math.PI);
  const rawLngSpan = east - west;
  const lngFraction = (rawLngSpan < 0 ? rawLngSpan + 360 : rawLngSpan) / 360;

  const zoomFor = (px: number, fraction: number) =>
    fraction > 0 ? Math.log2(px / WORLD_PX / fraction) : Infinity;

  const zoom = Math.min(
    zoomFor(FIT_VIEWPORT.height - FIT_PADDING_PX * 2, latFraction),
    zoomFor(FIT_VIEWPORT.width - FIT_PADDING_PX * 2, lngFraction)
  );
  // A degenerate boundary (a point, or none) leaves zoom at Infinity — fall
  // back to a regional view rather than storing a camera the map can't apply.
  if (!Number.isFinite(zoom)) return 11;
  return Math.round(Math.min(Math.max(zoom, 1), 18) * 100) / 100;
}

/** Whole-of-project camera, or null when the project has no usable boundary. */
export function cameraForBoundary(
  boundary: StoryBoundaryInput | null | undefined,
  overrides: Partial<StoryCamera> = {}
): StoryCamera | null {
  const feature = boundaryToFeature(boundary);
  if (!feature) return null;
  const bbox = bboxOf(feature);
  if (!bbox) return null;
  const [west, south, east, north] = bbox;
  return {
    center: [(west + east) / 2, (south + north) / 2],
    zoom: zoomForBbox(bbox),
    pitch: 0,
    bearing: 0,
    ...overrides,
  };
}

/** A closer camera on the same centre, for scenes that follow a wide opener. */
function zoomedInOn(
  camera: StoryCamera | null,
  delta: number,
  overrides: Partial<StoryCamera> = {}
): StoryCamera | null {
  if (!camera) return null;
  return {
    ...camera,
    zoom: Math.round(Math.min(camera.zoom + delta, 18) * 100) / 100,
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// Template definitions
// ---------------------------------------------------------------------------

export type StoryTemplateKey =
  | "guided"
  | "before_after"
  | "simulation"
  | "risk_briefing"
  | "regional_overview"
  | "blank";

/** A project input a template needs; unmet ones disable the tile. */
export type StoryTemplateRequirement = "strategy" | "simulation";

export interface StoryTemplateStrategy {
  id: string | number;
  name: string;
}

export interface StoryTemplateSimulation {
  simulation_id?: string;
  name?: string;
  is_ensemble?: boolean;
}

/** A project a folder or organisation-wide story may draw from. */
export interface StoryTemplateProject {
  id: number;
  name: string;
  bbox: Bbox | null;
  /** The strategy that describes the project as it is, when one is set. */
  operationalStrategyId: number | null;
  /** Most recently updated first. */
  updatedAt?: string | null;
}

export interface StoryTemplateContext {
  authorName?: string;
  /** Project stories: the project; folder stories: the folder; root: none. */
  projectName?: string;
  /** Which projects the story draws from (default "project"). */
  scope?: StoryScope;
  folderName?: string;
  /** Folder and organisation-wide stories: the projects in scope. */
  projects?: StoryTemplateProject[];
  boundary: StoryBoundaryInput | null | undefined;
  strategies: StoryTemplateStrategy[];
  simulations: StoryTemplateSimulation[];
  /** Today as YYYY-MM-DD — the fuel-state date templated scenes start on. */
  today: string;
  /** The author's measurement system — placeholder units are seeded in it. */
  unitSystem?: UnitSystem;
}

export interface StoryTemplate {
  key: StoryTemplateKey;
  name: string;
  /** One line for the picker tile. */
  summary: string;
  /** Scene-by-scene outline, shown on the tile so the shape is visible up front. */
  outline: string[];
  requires: StoryTemplateRequirement[];
  /** Which story scopes the template is offered for. */
  scopes: StoryScope[];
  build: (ctx: StoryTemplateContext) => StoryContent;
}

/** One scene per project after the overview, at most — a regional story is an overview, not a catalogue. */
export const REGIONAL_MAX_PROJECT_SCENES = 12;

const PROJECT_SCOPES: StoryScope[] = ["project"];
const SCOPED: StoryScope[] = ["folder", "root"];

// ----- block helpers -------------------------------------------------------

const heading = (text: string): StoryBlock => ({ ...newBlock("heading"), text } as StoryBlock);

const text = (html: string): StoryBlock => ({ ...newBlock("text"), html } as StoryBlock);

const metrics = (
  ctx: StoryTemplateContext,
  items: { label: string; units: string }[]
): StoryBlock =>
  ({
    ...newBlock("metrics_row"),
    items: items.map((item) => ({
      label: item.label,
      value: "",
      // Placeholder units are written metric here; seed them in the
      // author's own measurement system.
      units: toDisplayUnitLabel(item.units, ctx.unitSystem ?? "metric"),
      source: { kind: "manual" as const },
    })),
  } as StoryBlock);

/** A scene with template overrides applied over the builder's own defaults. */
const scene = (index: number, patch: Partial<StoryScene>): StoryScene => ({
  ...newScene(index),
  ...patch,
});

const content = (ctx: StoryTemplateContext, scenes: StoryScene[]): StoryContent => ({
  ...defaultStoryContent(ctx.authorName),
  subtitle: ctx.projectName ? `${ctx.projectName}` : "",
  scenes,
});

const entryId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `entry-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** A layer entry naming its project (folder and organisation-wide stories). */
const projectEntry = (projectId: number, patch: Partial<SceneLayerEntry> & { key: SceneLayerEntry["key"] }): SceneLayerEntry => ({
  id: entryId(),
  projectId,
  ...patch,
});

/** Whole-of-scope camera for a folder or organisation-wide story. */
function cameraForProjects(projects: StoryTemplateProject[]): StoryCamera | null {
  const extent = unionBbox(projects.map((p) => p.bbox));
  return extent ? cameraForBoundary(bboxPolygon(extent)) : null;
}

// ---------------------------------------------------------------------------

export const STORY_TEMPLATES: StoryTemplate[] = [
  {
    key: "regional_overview",
    name: "Regional overview",
    summary:
      "Every project in scope on one map, then a scene per project. The starting point for a story that spans a folder.",
    outline: ["All projects at a glance", "One scene per project"],
    requires: [],
    scopes: SCOPED,
    build: (ctx) => {
      const projects = [...(ctx.projects ?? [])].sort(
        (a, b) => Date.parse(b.updatedAt ?? "") - Date.parse(a.updatedAt ?? "") || a.name.localeCompare(b.name)
      );
      const where = ctx.scope === "folder" && ctx.folderName ? `the ${ctx.folderName} folder` : "your organisation";
      const shown = projects.slice(0, REGIONAL_MAX_PROJECT_SCENES);
      const scenes: StoryScene[] = [
        scene(0, {
          name: "Overview",
          kicker: "01 · Overview",
          camera: cameraForProjects(projects),
          layers: [],
          layerStack: projects.map((p) => projectEntry(p.id, { key: "boundary" })),
          legend: { enabled: true },
          blocks: [
            heading(`${projects.length} project${projects.length === 1 ? "" : "s"} in ${where}`),
            text(
              "<p>Every project this story draws on, outlined on one map. Each layer " +
                "below names the project it comes from; add treatments, fuel or " +
                "simulations from any of them.</p>" +
                (projects.length > shown.length
                  ? `<p>The scenes that follow cover the ${shown.length} most recently updated projects; add scenes for the rest as you need them.</p>`
                  : "")
            ),
          ],
        }),
        ...shown.map((p, i) =>
          scene(i + 1, {
            name: p.name,
            kicker: `${String(i + 2).padStart(2, "0")} · ${p.name}`,
            camera: p.bbox ? cameraForBoundary(bboxPolygon(p.bbox)) : null,
            layers: [],
            layerStack: [
              projectEntry(p.id, { key: "boundary" }),
              ...(p.operationalStrategyId !== null
                ? [projectEntry(p.id, { key: "treatments", strategyId: p.operationalStrategyId })]
                : []),
            ],
            legend: { enabled: true },
            blocks: [
              heading(p.name),
              text(
                p.operationalStrategyId !== null
                  ? "<p>The project's boundary and the treatments of its operational strategy. Say what matters here.</p>"
                  : "<p>The project's boundary. Add this project's treatments, fuel state or a simulation from the layer list.</p>"
              ),
            ],
          })
        ),
      ];
      return {
        ...defaultStoryContent(ctx.authorName, true),
        subtitle: ctx.scope === "folder" ? ctx.folderName ?? "" : "",
        scenes,
      };
    },
  },
  {
    key: "guided",
    name: "Guided example",
    summary:
      "A working four-scene story whose text explains the builder as you scroll it. The fastest way to learn the tool.",
    outline: [
      "How a story map works",
      "A map that changes per scene",
      "Before and after, side by side",
      "A simulation, animated",
    ],
    requires: [],
    scopes: PROJECT_SCOPES,
    build: (ctx) => {
      const wide = cameraForBoundary(ctx.boundary);
      const strategy = ctx.strategies[0] ?? null;
      const strategyId = strategy ? Number(strategy.id) : null;
      const simulation = ctx.simulations[0] ?? null;

      const scenes: StoryScene[] = [
        scene(0, {
          name: "How a story map works",
          kicker: "01 · Start here",
          camera: wide,
          layers: ["boundary"],
          blocks: [
            heading("This is a scene"),
            text(
              "<p>A story map is a series of <strong>scenes</strong>. Readers scroll, " +
                "and the map beside them moves to each scene's saved camera position " +
                "in turn. This text is a block inside scene 1.</p>" +
                "<p>Edit any of this. The scenes below show what the builder can do — " +
                "delete the ones you don't need.</p>"
            ),
          ],
        }),
        scene(1, {
          name: "A map that changes",
          kicker: "02 · Layers",
          camera: zoomedInOn(wide, 1.5, { pitch: 35 }),
          layers: strategyId
            ? ["boundary", "treatments", "fuel_load"]
            : ["boundary", "fire_history"],
          strategyId,
          date: strategyId ? ctx.today : null,
          blocks: [
            heading("Each scene sets its own map"),
            text(
              "<p>Every scene stores its own camera, its own layers and its own " +
                "date. This one is zoomed in and tilted, and it turns on " +
                (strategyId
                  ? "treatments and fuel load for the selected strategy."
                  : "fire history.") +
                "</p>" +
                "<p>Use the right-hand rail to change what this scene shows. " +
                "<strong>Use current view</strong> saves wherever you've dragged " +
                "the map to as this scene's camera.</p>"
            ),
          ],
        }),
        scene(2, {
          name: "Before and after",
          kicker: "03 · Compare",
          camera: zoomedInOn(wide, 1),
          layers: ["boundary"],
          mode: "compare",
          compare: {
            before: {
              label: "BASELINE",
              strategyId: null,
              date: ctx.today,
              layers: strategyId ? ["fuel_load"] : [],
            },
            after: {
              label: strategy ? strategy.name.toUpperCase() : "TREATED",
              strategyId,
              date: ctx.today,
              layers: strategyId ? ["fuel_load", "treatments"] : [],
            },
          },
          blocks: [
            heading("Two landscapes, one map"),
            text(
              "<p>A scene set to <strong>compare</strong> mode splits the map in " +
                "two and gives the reader a handle to drag between them. Each side " +
                "is its own landscape state — a strategy and a date.</p>" +
                (strategyId
                  ? ""
                  : "<p>This project has no strategies yet, so both sides are empty. " +
                    "Pick strategies for each side in the right-hand rail once you " +
                    "have some.</p>")
            ),
          ],
        }),
      ];

      if (simulation?.simulation_id) {
        scenes.push(
          scene(3, {
            name: "A simulation, animated",
            kicker: "04 · Animation",
            camera: zoomedInOn(wide, 0.5),
            layers: ["boundary", "assets"],
            mode: "animation",
            animations: [
              {
                ...defaultSceneAnimation(),
                simulationId: simulation.simulation_id,
                view: simulation.is_ensemble ? "confidence" : "single",
              },
            ],
            blocks: [
              heading("Fire, over time"),
              text(
                "<p>An <strong>animation</strong> scene plays a completed fire " +
                  "simulation over the map. " +
                  (simulation.is_ensemble
                    ? "This one uses the ensemble's burn probability, so the reader " +
                      "sees where fire went across every member rather than one run."
                    : "Click an ignition marker on the map to choose which ignition " +
                      "point this scene follows.") +
                  "</p>"
              ),
            ],
          })
        );
      } else {
        scenes.push(
          scene(3, {
            name: "Where the numbers go",
            kicker: "04 · Metrics",
            camera: zoomedInOn(wide, 0.5),
            layers: ["boundary", "assets"],
            blocks: [
              heading("Where the numbers go"),
              text(
                "<p>Metric blocks put figures beside the map. Fill these in, or " +
                  "delete the block.</p>" +
                  "<p>Once this project has a completed simulation you can also add " +
                  "an <strong>animation</strong> scene that plays the fire over the " +
                  "map.</p>"
              ),
              metrics(ctx, [
                { label: "Area treated", units: "ha" },
                { label: "Assets protected", units: "" },
              ]),
            ],
          })
        );
      }

      return content(ctx, scenes);
    },
  },

  {
    key: "before_after",
    name: "Treatment before / after",
    summary:
      "Sets the scene, then puts the untreated and treated landscape side by side with room for the numbers.",
    outline: ["The area", "Before / after compare", "What changed"],
    requires: ["strategy"],
    scopes: PROJECT_SCOPES,
    build: (ctx) => {
      const wide = cameraForBoundary(ctx.boundary);
      const strategy = ctx.strategies[0] ?? null;
      const strategyId = strategy ? Number(strategy.id) : null;

      return content(ctx, [
        scene(0, {
          name: "The area",
          kicker: "01 · Context",
          camera: wide,
          layers: ["boundary", "assets", "fire_history"],
          blocks: [
            heading(ctx.projectName ?? "The area"),
            text(
              "<p>Describe the landscape and why it matters — what's in it, what " +
                "has burnt before, who is exposed.</p>"
            ),
          ],
        }),
        scene(1, {
          name: "Before and after",
          kicker: "02 · The treatment",
          camera: zoomedInOn(wide, 1.5),
          layers: ["boundary"],
          mode: "compare",
          compare: {
            before: {
              label: "BEFORE",
              strategyId: null,
              date: ctx.today,
              layers: ["fuel_load"],
            },
            after: {
              label: strategy ? strategy.name.toUpperCase() : "AFTER",
              strategyId,
              date: ctx.today,
              layers: ["fuel_load", "treatments"],
            },
          },
          blocks: [
            heading("What the treatment does"),
            text(
              "<p>Drag the handle to compare. Explain what was treated, where, and " +
                "why those locations.</p>"
            ),
          ],
        }),
        scene(2, {
          name: "What changed",
          kicker: "03 · Outcome",
          camera: zoomedInOn(wide, 1),
          layers: ["boundary", "treatments"],
          strategyId,
          date: ctx.today,
          blocks: [
            heading("What changed"),
            metrics(ctx, [
              { label: "Area treated", units: "ha" },
              { label: "Fuel load reduction", units: "t/ha" },
              { label: "Assets protected", units: "" },
            ]),
            text("<p>Close with what this means for the coming season.</p>"),
          ],
        }),
      ]);
    },
  },

  {
    key: "simulation",
    name: "Simulation walkthrough",
    summary:
      "Opens on the landscape, plays a completed fire simulation over it, then lands on the consequences.",
    outline: ["The landscape", "The fire, animated", "Impact"],
    requires: ["simulation"],
    scopes: PROJECT_SCOPES,
    build: (ctx) => {
      const wide = cameraForBoundary(ctx.boundary);
      const simulation = ctx.simulations[0] ?? null;
      const strategyId = ctx.strategies[0] ? Number(ctx.strategies[0].id) : null;

      return content(ctx, [
        scene(0, {
          name: "The landscape",
          kicker: "01 · Setting",
          camera: wide,
          layers: ["boundary", "assets", "vegetation"],
          strategyId,
          date: strategyId ? ctx.today : null,
          blocks: [
            heading("The landscape"),
            text(
              "<p>Set up the run: the conditions, the ignition, and what's at " +
                "stake if it gets away.</p>"
            ),
          ],
        }),
        scene(1, {
          name: "The fire",
          kicker: "02 · Simulation",
          camera: zoomedInOn(wide, 0.5),
          layers: ["boundary", "assets"],
          mode: "animation",
          animations: simulation?.simulation_id
            ? [
                {
                  ...defaultSceneAnimation(),
                  simulationId: simulation.simulation_id,
                  view: simulation.is_ensemble ? "confidence" : "single",
                },
              ]
            : [defaultSceneAnimation()],
          blocks: [
            heading(simulation?.name || "The fire"),
            text(
              "<p>Narrate the run — the first few hours, where it accelerates, " +
                "where it reaches assets.</p>" +
                (simulation?.is_ensemble
                  ? "<p>This scene shows the ensemble's burn probability. Switch it " +
                    "to a percentile in the right-hand rail to show a single " +
                    "representative run instead.</p>"
                  : "<p>Click an ignition marker on the map to pick which ignition " +
                    "this scene follows.</p>")
            ),
          ],
        }),
        scene(2, {
          name: "Impact",
          kicker: "03 · Impact",
          camera: zoomedInOn(wide, 1.5),
          layers: ["boundary", "assets"],
          blocks: [
            heading("Impact"),
            metrics(ctx, [
              { label: "Area burnt", units: "ha" },
              { label: "Assets affected", units: "" },
              { label: "Time to first impact", units: "hrs" },
            ]),
            text("<p>What follows from this — the decision you want made.</p>"),
          ],
        }),
      ]);
    },
  },

  {
    key: "risk_briefing",
    name: "Risk briefing",
    summary:
      "A standing brief: the area, what's exposed, the current fuel state, and where to act first.",
    outline: ["The area", "What's exposed", "Fuel state", "Priorities"],
    requires: [],
    scopes: PROJECT_SCOPES,
    build: (ctx) => {
      const wide = cameraForBoundary(ctx.boundary);
      const strategyId = ctx.strategies[0] ? Number(ctx.strategies[0].id) : null;

      return content(ctx, [
        scene(0, {
          name: "The area",
          kicker: "01 · Overview",
          camera: wide,
          layers: ["boundary", "fire_history"],
          blocks: [
            heading(ctx.projectName ?? "The area"),
            text("<p>Where this is, and the fire history that frames it.</p>"),
          ],
        }),
        scene(1, {
          name: "What's exposed",
          kicker: "02 · Assets",
          camera: zoomedInOn(wide, 1),
          layers: ["boundary", "assets"],
          blocks: [
            heading("What's exposed"),
            metrics(ctx, [
              { label: "Assets in area", units: "" },
              { label: "Highest-value asset", units: "" },
            ]),
            text("<p>Who and what is at risk, and how badly.</p>"),
          ],
        }),
        scene(2, {
          name: "Fuel state",
          kicker: "03 · Fuel",
          camera: zoomedInOn(wide, 0.5),
          layers: ["boundary", "fuel_load", "vegetation"],
          strategyId,
          date: ctx.today,
          blocks: [
            heading("Fuel state"),
            text(
              "<p>The current fuel load across the area. " +
                (strategyId
                  ? "Change the scene date in the right-hand rail to show how it " +
                    "builds through the season."
                  : "Pick a strategy and date in the right-hand rail to load the " +
                    "fuel and vegetation layers.") +
                "</p>"
            ),
          ],
        }),
        scene(3, {
          name: "Priorities",
          kicker: "04 · Action",
          camera: zoomedInOn(wide, 1),
          layers: ["boundary", "treatments"],
          strategyId,
          date: strategyId ? ctx.today : null,
          blocks: [
            heading("Where to act first"),
            text("<p>The recommendation, and what it costs to do nothing.</p>"),
          ],
        }),
      ]);
    },
  },

  {
    key: "blank",
    name: "Blank story",
    summary: "One empty scene. Build it from scratch.",
    outline: ["One empty scene"],
    requires: [],
    scopes: ["project", "folder", "root"],
    build: (ctx) => defaultStoryContent(ctx.authorName, (ctx.scope ?? "project") !== "project"),
  },
];

/** The templates offered for a story of this scope. */
export function templatesForScope(scope: StoryScope | undefined): StoryTemplate[] {
  const kind = scope ?? "project";
  return STORY_TEMPLATES.filter((t) => t.scopes.includes(kind));
}

/** Why a template can't be used here, or null when it can. */
export function templateBlockedReason(
  template: StoryTemplate,
  ctx: StoryTemplateContext
): string | null {
  if (template.requires.includes("strategy") && ctx.strategies.length === 0) {
    return "Needs at least one mitigation strategy in this project.";
  }
  if (template.requires.includes("simulation") && ctx.simulations.length === 0) {
    return "Needs at least one completed simulation in this project.";
  }
  return null;
}

/** Default title for a story started from a template. */
export function templateTitle(
  template: StoryTemplate,
  projectName?: string
): string {
  if (template.key === "blank") return "Untitled story";
  if (template.key === "regional_overview") return projectName ? `${projectName} — Overview` : "Regional overview";
  if (!projectName) return template.name;
  return `${projectName} — ${template.name}`;
}
